import { useCallback, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { trackEvent } from '../lib/gamification';
import { handlePlanLimitError } from '../lib/planLimit';
import {
  ArrowLeft, FileText, Wand2, Download, Copy, Check, Loader2,
  XCircle, Building2, CreditCard, HelpCircle, AlertTriangle, FileCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const templateIcons = {
  resiliation: XCircle,
  reclamation: AlertTriangle,
  demande_aide: HelpCircle,
  attestation: FileCheck,
  mise_en_demeure: CreditCard
};

const templateColors = {
  resiliation: 'text-red-400 bg-red-500/20',
  reclamation: 'text-orange-400 bg-orange-500/20',
  demande_aide: 'text-blue-400 bg-blue-500/20',
  attestation: 'text-green-400 bg-green-500/20',
  mise_en_demeure: 'text-purple-400 bg-purple-500/20'
};

export const LettresPage = () => {
  const { isDark } = useTheme();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [templates, setTemplates] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [fields, setFields] = useState({});
  const [tone, setTone] = useState('formel');
  const [generating, setGenerating] = useState(false);
  const [generatedLetter, setGeneratedLetter] = useState(null);
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState([]);

  const loadTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/letters/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || {});
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  }, [token]);

  const loadHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/letters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data.letters || []);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadTemplates();
      loadHistory();
    }
  }, [token, loadTemplates, loadHistory]);

  const handleGenerate = async () => {
    if (!selectedTemplate) {
      toast.error('Sélectionne un type de lettre');
      return;
    }
    
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/letters/generate`,
        {
          template_type: selectedTemplate,
          fields: fields,
          tone: tone,
          user_name: user?.first_name ? `${user.first_name} ${user.last_name || ''}` : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setGeneratedLetter(response.data);
        toast.success('Lettre générée !');
        loadHistory();
        trackEvent(token, 'first_letter');
      }
    } catch (error) {
      if (!handlePlanLimitError(error, navigate)) {
        toast.error('Erreur lors de la génération');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (generatedLetter?.letter_text) {
      navigator.clipboard.writeText(generatedLetter.letter_text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Copié dans le presse-papier');
    }
  };

  const handleDownloadPDF = async (letterId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/letters/${letterId}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lettre_${letterId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const selectedTemplateData = selectedTemplate ? templates[selectedTemplate] : null;

  return (
    <div className={`min-h-screen px-4 py-6 transition-colors duration-300 ${isDark ? 'bg-[#0F0F1A]' : 'bg-[#FAFBFC]'}`} data-testid="lettres-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <Link 
          to="/dashboard" 
          className={`inline-flex items-center gap-2 mb-6 transition-colors ${
            isDark ? 'text-[#94A3B8] hover:text-[#81d8d0]' : 'text-gray-500 hover:text-[#81d8d0]'
          }`}
        >
          <ArrowLeft size={18} />
          <span>Retour au dashboard</span>
        </Link>

        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
              <Wand2 className="w-6 h-6 text-purple-400" />
            </div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Lettre Magique
            </h1>
          </div>
          <p className={`mb-8 ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
            Génère des courriers administratifs professionnels en quelques clics
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left - Form */}
          <div className="space-y-6">
            {/* Template Selection */}
            <Card className={`p-6 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                1. Choisis ton type de lettre
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.entries(templates).map(([key, template]) => {
                  const Icon = templateIcons[key] || FileText;
                  const colorClass = templateColors[key] || 'text-gray-400 bg-gray-500/20';
                  const isSelected = selectedTemplate === key;
                  
                  return (
                    <button
                      key={key}
                      onClick={() => {
                        setSelectedTemplate(key);
                        setFields({});
                        setGeneratedLetter(null);
                      }}
                      className={`p-4 rounded-xl text-left transition-all ${
                        isSelected
                          ? isDark 
                            ? 'bg-[#81d8d0]/20 border-2 border-[#81d8d0]' 
                            : 'bg-[#81d8d0]/20 border-2 border-[#81d8d0]'
                          : isDark
                            ? 'bg-white/5 border-2 border-transparent hover:bg-white/10'
                            : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${colorClass}`}>
                        <Icon size={20} />
                      </div>
                      <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {template.title}
                      </h4>
                      <p className={`text-xs mt-1 ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>
                        {template.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </Card>

            {/* Fields */}
            {selectedTemplateData && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <Card className={`p-6 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                  <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    2. Remplis les informations
                  </h3>
                  <div className="space-y-4">
                    {selectedTemplateData.required_fields?.map(field => (
                      <div key={field}>
                        <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>
                          {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} *
                        </Label>
                        {field === 'motif' || field === 'situation' ? (
                          <Textarea
                            value={fields[field] || ''}
                            onChange={(e) => setFields({ ...fields, [field]: e.target.value })}
                            className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}
                            placeholder={`Décris ${field.replace(/_/g, ' ')}...`}
                            rows={3}
                          />
                        ) : (
                          <Input
                            value={fields[field] || ''}
                            onChange={(e) => setFields({ ...fields, [field]: e.target.value })}
                            className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}
                            placeholder={`Ex: ${field === 'service_name' ? 'Netflix' : field === 'montant' ? '50€' : '...'}`}
                          />
                        )}
                      </div>
                    ))}
                    
                    <div>
                      <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Ton de la lettre</Label>
                      <Select value={tone} onValueChange={setTone}>
                        <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="formel">Formel (recommandé)</SelectItem>
                          <SelectItem value="semi-formel">Semi-formel</SelectItem>
                          <SelectItem value="cordial">Cordial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <Button
                    onClick={handleGenerate}
                    disabled={generating}
                    className="w-full mt-6 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-6"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        Génération en cours...
                      </>
                    ) : (
                      <>
                        <Wand2 size={20} className="mr-2" />
                        Générer ma lettre
                      </>
                    )}
                  </Button>
                </Card>
              </motion.div>
            )}
          </div>

          {/* Right - Result */}
          <div className="space-y-6">
            {generatedLetter ? (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                <Card className={`p-6 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Ta lettre est prête !
                    </h3>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCopy}
                        className={isDark ? 'border-white/20' : ''}
                      >
                        {copied ? <Check size={16} className="mr-1 text-green-500" /> : <Copy size={16} className="mr-1" />}
                        {copied ? 'Copié' : 'Copier'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadPDF(generatedLetter.letter_id)}
                        className={isDark ? 'border-white/20' : ''}
                      >
                        <Download size={16} className="mr-1" />
                        PDF
                      </Button>
                    </div>
                  </div>
                  
                  <div className={`p-4 rounded-lg font-mono text-sm whitespace-pre-wrap max-h-[500px] overflow-y-auto ${
                    isDark ? 'bg-black/30 text-[#e2e8f0]' : 'bg-gray-50 text-gray-800'
                  }`}>
                    {generatedLetter.letter_text}
                  </div>
                </Card>
              </motion.div>
            ) : (
              <Card className={`p-12 border border-dashed text-center ${
                isDark ? 'bg-white/5 border-white/20' : 'bg-gray-50 border-gray-300'
              }`}>
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  isDark ? 'bg-purple-500/10' : 'bg-purple-100'
                }`}>
                  <FileText className="w-8 h-8 text-purple-400" />
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Ta lettre apparaîtra ici
                </h3>
                <p className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>
                  Choisis un type de lettre et remplis les informations pour commencer
                </p>
              </Card>
            )}

            {/* History */}
            {history.length > 0 && (
              <Card className={`p-6 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Historique ({history.length})
                </h3>
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {history.slice(0, 5).map(letter => {
                    const template = templates[letter.template_type];
                    const Icon = templateIcons[letter.template_type] || FileText;
                    
                    return (
                      <div
                        key={letter.id}
                        className={`p-3 rounded-lg flex items-center justify-between ${
                          isDark ? 'bg-white/5' : 'bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon size={18} className={isDark ? 'text-[#94A3B8]' : 'text-gray-400'} />
                          <div>
                            <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {template?.title || letter.template_type}
                            </p>
                            <p className={`text-xs ${isDark ? 'text-[#64748B]' : 'text-gray-500'}`}>
                              {new Date(letter.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadPDF(letter.id)}
                        >
                          <Download size={14} />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LettresPage;
